import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, FormControlLabel, Switch } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { dialerAgentListAtom, flowValidationAtom } from '../../../../Services/_states';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { Handle, Position } from 'reactflow';
import { InterveneActions } from '../../../../Services/_actions/Intervene/intervene.actions';
import RenderTextArea from './RenderTextArea.tsx';
interface SuggestionObj {
  label: string;
  value: string;
  paramType?: string;
}

const RequestInterventionNode = ({ data }: any) => {
  const actions = InterveneActions();
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const [bodyText, setBodyText] = useState(data?.body || '');
  const dialerAgents: any = useRecoilValue(dialerAgentListAtom);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const textareaRef = useRef < HTMLTextAreaElement > (null);
  const [cursorPosition, setCursorPosition] = useState < number | null > (null);
  const [enabledCount, setEnabledCount] = useState(0);
  const [customButtonCount, setCustomButtonCount] = useState(0);

  useEffect(() => {
    const countEnabled = data?.intervention?.filter((button) => button?.enabled && button.type !== "custom").length;
    const customBtnCount = data?.intervention?.filter((button) => button?.enabled && button.type === "custom").length;
    setCustomButtonCount(customBtnCount);
    setEnabledCount(countEnabled);
  }, [data?.intervention]);

  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      // const query = match[1]; // Get the text after `{{@`
      // setFilteredSuggestions(suggestions.filter((s) =>s.label.toLowerCase().includes(query.toLowerCase())));
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // Update `params` by finding all valid `{{label}}` entries in the text
    const matches = value.match(/{{(.*?)}}/g); // Match all `{{label}}`
    const labelsInBody = matches
      ? matches.map((match) => match.replace(/{{|}}/g, ""))
      : [];

    // Categorize params by `paramType`
    const categorizedParams: any = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggestionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === "global") {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === "result") {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === "webform") {
          categorizedParams.webform.push(result.value);
        }
      }
    });
    data.onChange(data.id, { body: value, params: categorizedParams });
  };



  const handleSuggestionClick = (suggestion: SuggestionObj) => {
    if (cursorPosition !== null) {
      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);
      const updatedText =
        textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) +
        textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      // Initialize parameters if not present
      const updatedParams = {
        global: data.params?.global || [],
        result: data.params?.result || [],
        webform: data.params?.webform || [],
      };
      if (suggestion.paramType === "global") {
        updatedParams.global = [...updatedParams.global, suggestion.value];
      } else if (suggestion.paramType === "result") {
        updatedParams.result = [...updatedParams.result, suggestion.value];
      } else if (suggestion.paramType === "webform") {
        updatedParams.webform = [...updatedParams.webform, suggestion.value];
      }
      data.onChange(data.id, { body: updatedText, params: updatedParams });
    }
  };


  useEffect(() => {
    const fetchDialerAgents = async () => {
      try {
        const response = await actions.getDialerAgents();
        // if (response?.data?.data) {
        //   const formattedAgents = response?.data?.data.map(agent => ({
        //     label: agent.name,  
        //     value: agent.id      
        //   }));
        //   setDialerAgents(formattedAgents);
        // }
      } catch (error) {
        console.error("Error fetching dialer agents", error);
      }
    };
    fetchDialerAgents();
  }, []);

  const handleToggle = (index) => {
    const updatedButtons = [...(data?.intervention || [])];
    updatedButtons[index] = {
      ...updatedButtons[index],
      enabled: !updatedButtons[index].enabled
    };
    const enabledButtonsCount = updatedButtons.filter(
      button => button.enabled && button.type !== 'custom'
    ).length;
    const totalCustomButtons = updatedButtons.filter((item) => item?.type === 'custom').length;

    if (enabledButtonsCount === 3) {
      const updatedButtonsWithoutCustom = updatedButtons.filter(button => button.type !== 'custom');
      data.onChange(data.id, { intervention: updatedButtonsWithoutCustom });
    } else if (enabledButtonsCount + totalCustomButtons > 3) {
      const customObjects = updatedButtons.filter(item => item.type === 'custom');
      const removeCount = Math.min(enabledButtonsCount + totalCustomButtons - 3 || 0, customObjects.length);
      let removedCount = 0;
      const updatedArray = updatedButtons.filter(item => {
        if (item.type === 'custom' && removedCount < removeCount) {
          removedCount++;
          return false;
        }
        return true;
      });
      data.onChange(data.id, { intervention: updatedArray });
    } else {
      data.onChange(data.id, { intervention: updatedButtons });
    }
  };

  const handleAddCustomButtons = () => {
    const currentCustomButtonsCount = (data?.intervention || []).filter((item) => item?.type === 'custom').length;
    const newButton = {
      type: 'custom',
      value: `Custom Button ${currentCustomButtonsCount + 1}`,
      label: `Custom Button ${currentCustomButtonsCount + 1}`,
      enabled: true
    };
    const updatedButtons = [...(data?.intervention || []), newButton];
    const enableButtonsCount = (updatedButtons || []).filter((item) => item?.type !== 'custom' && item?.enabled).length;
    console.log(updatedButtons, "updatedButtonsupdatedButtonsupdatedButtons")

    if (currentCustomButtonsCount + enableButtonsCount < 3) {
      data.onChange(data.id, { intervention: updatedButtons });
    }
  };

  return (
    <div style={{ padding: '10px', background: '#f9f9f9', borderRadius: '6px' }}>
      <label>Header:</label>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={data.header || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length <= 60) {
              data.onChange(data.id, { header: newValue });
            }
          }}
          placeholder="Enter Header Text"
          style={{
            width: '100%',
            padding: '5px',
            paddingRight: '50px', // Reserve space for character count
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f5f5f5',
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: data.header?.length === 60 ? 'red' : '#888',
          }}
        >
          {`${(data.header || '').length}/60`}
        </span>
      </div>
      <Typography variant="body2" color="error">{flowValidationObj[data?.id]?.header}</Typography>
      <RenderTextArea
        data={data}
      />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}

      {/* {showSuggestions && data?.parentResults?.length > 0 && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            zIndex: 1000,
            width: '100%',
          }}
        >
          {data?.parentResults?.map((suggestion: SuggestionObj, index: number) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '5px 10px',
                cursor: 'pointer',
                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
              }}
            >
              {suggestion.label}
            </div>
          ))}
        </div>
      )} */}
      <label>Footer:</label>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={data.footer || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            if (newValue.length <= 60) {
              data.onChange(data.id, { footer: newValue });
            }
          }}
          placeholder="Enter Footer Text"
          style={{
            width: '100%',
            padding: '5px',
            paddingRight: '50px', // Reserve space for character count
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f5f5f5',
          }}
        />
        <span
          style={{
            position: 'absolute',
            right: '10px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '12px',
            color: data.footer?.length === 60 ? 'red' : '#888',
          }}
        >
          {`${(data.footer || '').length}/60`}
        </span>
      </div>
      <Typography variant="body2" color="error">{flowValidationObj[data?.id]?.footer}</Typography>
      {/* <div>
        {data?.intervention?.map((button, index) => (
          <div key={`${data?.id}-${index}`}>
            <FormControlLabel
              control={
                <Switch
                  checked={button.enabled}
                  onChange={() => handleToggle(index)}
                />
              }
              label={button?.label}
              labelPlacement="start"
            />

            {button.enabled && (
              <>
                {button?.type === "click_to_call" &&
                  <>
                    <SearchableDropdown
                      label="Select Agents"
                      labelKey='name'
                      valueKey='id'
                      options={dialerAgents?.data || []}
                      value={data?.agentList || null}
                      onChange={(value: any) => { data.onChange(data.id, { agentList: value }); }}

                      multiSelect={true}
                      sx={{ pb: 1 }}
                    />
                    {flowValidationObj[data?.id]?.agentList &&
                      <Typography variant="body2" color="error">{flowValidationObj[data?.id]?.agentList}</Typography>
                    }
                  </>
                }
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    position: 'relative',
                  }}
                >
                  <input
                    type="text"
                    value={button?.value || ''}
                    placeholder={button?.label || ''}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 20);
                      const updatedButtonLabels = [...(data?.intervention || [])];
                      updatedButtonLabels[index] = { ...updatedButtonLabels[index], value: newValue };
                      data.onChange(data.id, { intervention: updatedButtonLabels });
                    }}
                    style={{
                      flex: 1,
                      padding: '5px',
                      paddingRight: '50px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      background: '#f5f5f5',
                      height: '40px',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '24px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      color: button.value.length === 20 ? 'red' : '#888',
                    }}
                  >
                    {`${button.value.length}/20`}
                  </span>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`${data?.id}-${button.type}`}
                    style={{
                      background: '#555',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      position: 'absolute',
                      right: '-20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </div>
              </>
            )}
            {(data?.intervention || []).length > 0 &&
              flowValidationObj[data?.id]?.valueError &&
              flowValidationObj[data?.id]?.valueError[index] && (
                <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                  {flowValidationObj[data?.id]?.valueError[index]}
                </Typography>
              )}
            {(data?.intervention || []).length > 0 &&
              flowValidationObj[data?.id]?.connectionError &&
              flowValidationObj[data?.id]?.connectionError[index] && (
                <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                  {flowValidationObj[data?.id]?.connectionError[index]}
                </Typography>
              )}
          </div>
        ))}
      </div> */}
      <div>
        {data?.intervention?.map((button, index) => (
          <div key={`${data?.id}-${index}`}>
            {/* Toggle Switch */}
            {button?.type !== "custom" &&
              <FormControlLabel
                control={<Switch checked={button.enabled} onChange={() => handleToggle(index)} />}
                label={button?.label}
                labelPlacement="start"
              />
            }
            {/* Button and Input if enabled */}
            {button.enabled && (
              <>
                {button?.type === 'click_to_call' && (
                  <>
                    <SearchableDropdown
                      label="Select Agents"
                      labelKey="name"
                      valueKey="id"
                      options={dialerAgents?.data || []}
                      value={data?.agentList || null}
                      onChange={(value: any) => { data.onChange(data.id, { agentList: value }); }}
                      multiSelect={true}
                      sx={{ pb: 1 }}
                    />
                    {flowValidationObj[data?.id]?.agentList && (
                      <Typography variant="body2" color="error">{flowValidationObj[data?.id]?.agentList}</Typography>
                    )}
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', position: 'relative' }}>
                  <input
                    type="text"
                    value={button?.value || ''}
                    placeholder={button?.label || ''}
                    onChange={(e) => {
                      const newValue = e.target.value.slice(0, 20);
                      const updatedButtonLabels = [...(data?.intervention || [])];
                      updatedButtonLabels[index] = { ...updatedButtonLabels[index], value: newValue };
                      data.onChange(data.id, { intervention: updatedButtonLabels });
                    }}
                    style={{
                      flex: 1,
                      padding: '5px',
                      paddingRight: '50px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      background: '#f5f5f5',
                      height: '40px',
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      right: '24px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      color: button.value.length === 20 ? 'red' : '#888',
                    }}
                  >
                    {`${button.value.length}/20`}
                  </span>
                  <Handle
                    type="source"
                    position={Position.Right}
                    id={`${data?.id}-${button.value}`}
                    style={{
                      background: '#555',
                      height: '10px',
                      width: '10px',
                      borderRadius: '50%',
                      position: 'absolute',
                      right: '-20px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  />
                </div>
              </>
            )}
            {(data?.intervention || []).length > 0 &&
              flowValidationObj[data?.id]?.valueError &&
              flowValidationObj[data?.id]?.valueError[index] && (
                <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                  {flowValidationObj[data?.id]?.valueError[index]}
                </Typography>
              )}
            {(data?.intervention || []).length > 0 &&
              flowValidationObj[data?.id]?.connectionError &&
              flowValidationObj[data?.id]?.connectionError[index] && (
                <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
                  {flowValidationObj[data?.id]?.connectionError[index]}
                </Typography>
              )}
          </div>
        ))}
        {(enabledCount >= 1 && enabledCount < 3 && customButtonCount < 2) && (
          <Typography
            variant="body2"
            style={{
              marginTop: '10px',
              cursor: 'pointer',
              color: '#007bff',
              textDecoration: 'underline',
              fontSize: '14px',
            }}
            onClick={handleAddCustomButtons}
          >
            + Add Button
          </Typography>
        )}
      </div>

      {/* )} */}
      {flowValidationObj[data?.id]?.errorMsg &&
        <Typography variant="body2" color="error">{flowValidationObj[data?.id]?.errorMsg}</Typography>
      }
    </div>
  );
};

export default RequestInterventionNode;